import { useEffect, useState } from "react";
import SampleScannedNew from "./checkin/SampleScannedNew"; // Import the new component
import SampleScanned from "./checkin/sampleScanned"; // Keep the old one for non-ETK orders
import { GetUsers } from "../services/usersService";
import ScannerNew from "./common/ScannerNew";
import { NotificationManager } from "react-notifications";
import {
  Box,
  Container,
  Typography,
  Fade,
  LinearProgress,
  Card,
  CardContent,
  Button,
} from "@mui/material";
import { CheckCircle as SuccessIcon } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";

export default function MainNew() {
  const [scan, setScan] = useState("");
  const [webUrl, setWebUrl] = useState("");
  const [loading, setLoading] = useState(true);
  const [scannerActive, setScannerActive] = useState(false);
  const [isEtkOrder, setIsEtkOrder] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    // The user list itself is no longer needed here -- SampleScanned now uses
    // UserSelectorDialog, which fetches its own users. This call just gates
    // the scanner becoming active until the API is confirmed reachable.
    async function checkApiReady() {
      try {
        setLoading(true);
        await GetUsers("SHIPPING");
        setScannerActive(true);
      } catch (err) {
        NotificationManager.error(
          "Error getting the list of users from the API"
        );
      } finally {
        setLoading(false);
      }
    }
    checkApiReady();
  }, []);

  useEffect(() => {
    if (webUrl !== "") {
      console.log("webUrl", webUrl);
      window.open(webUrl, "_blank", "noreferrer");
    }
  }, [webUrl]);

  const onScan = (scanData) => {
    if (scanData.includes("Order Number:")) {
      const orderNumberRegex = /Order Number: (\d+)/;
      const orderNumberMatch = scanData.match(orderNumberRegex);
      const scannedOrderNumber = orderNumberMatch ? orderNumberMatch[1] : "";
      const webLinkRegex = /Web Link: (https?:\/\/\S+)/;
      const webLinkMatch = scanData.match(webLinkRegex);
      const webUrlScanned = webLinkMatch ? webLinkMatch[1] : "";

      if (webUrlScanned !== "") {
        setWebUrl(webUrlScanned);
        setIsEtkOrder(true); // This is an ETK order
      } else {
        setIsEtkOrder(false); // Not an ETK order
      }

      setScan(scannedOrderNumber);
      // Don't open new tab - handle it in the same component
    } else {
      setScan(scanData);
      setIsEtkOrder(false); // Regular scan, not ETK
      // Don't open new tab - handle it in the same component
    }
  };

  const clearScan = () => {
    setWebUrl("");
    setScan("");
    setIsEtkOrder(false);
    setScannerActive(true);
  };

  const renderScanner = () => {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          py: 2,
        }}
      >
        <Container maxWidth="lg">
          <Fade in={!scan} timeout={500}>
            <Card
              elevation={8}
              sx={{
                borderRadius: 3,
                overflow: "hidden",
                backdropFilter: "blur(10px)",
                backgroundColor: "rgba(255, 255, 255, 0.95)",
              }}
            >
              {/* Status Bar */}
              {loading && (
                <LinearProgress
                  sx={{
                    height: 3,
                    "& .MuiLinearProgress-bar": {
                      background: "linear-gradient(90deg, #667eea, #764ba2)",
                    },
                  }}
                />
              )}

              <CardContent sx={{ p: 0 }}>
                {/* Scanner Area */}
                <Box sx={{ p: 3 }}>
                  {scannerActive && !loading && <ScannerNew onScan={onScan} />}
                </Box>
              </CardContent>
            </Card>
          </Fade>
        </Container>
      </Box>
    );
  };

  const renderInputFields = () => {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          backgroundColor: "grey.50",
          py: 2,
        }}
      >
        <Container maxWidth="lg">
          <Fade in={!!scan} timeout={500}>
            <Card
              elevation={2}
              sx={{
                borderRadius: 2,
                overflow: "hidden",
              }}
            >
              <CardContent sx={{ p: 0 }}>
                {/* Processing Header */}
                <Box
                  sx={{
                    backgroundColor: "white",
                    borderBottom: 1,
                    borderColor: "divider",
                    p: 3,
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <Box
                    sx={{
                      backgroundColor: "success.main",
                      color: "white",
                      borderRadius: 2,
                      p: 1,
                      mr: 2,
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    <SuccessIcon sx={{ fontSize: 28 }} />
                  </Box>
                  <Box>
                    {!isEtkOrder && (
                      <>
                        <Typography
                          variant="h4"
                          component="h1"
                          fontWeight="700"
                          color="text.primary"
                        >
                          Processing Sample
                        </Typography>
                        <Typography
                          variant="h6"
                          color="text.secondary"
                          sx={{ mt: 0.5, fontFamily: "monospace" }}
                        >
                          ID: {scan}
                        </Typography>
                      </>
                    )}
                    {isEtkOrder && webUrl && (
                      <Button
                        variant="contained"
                        color="primary"
                        size="small"
                        href={webUrl}
                        target="_blank"
                        sx={{ mt: 1, textTransform: "none", borderRadius: 2 }}
                      >
                        Open ETK Form
                      </Button>
                    )}
                    <Typography
                      variant="body2"
                      color={isEtkOrder ? "primary.main" : "text.secondary"}
                      sx={{
                        mt: 0.5,
                        fontWeight: isEtkOrder ? "bold" : "normal",
                      }}
                    >
                      {isEtkOrder ? "ETK Order" : "Regular Order"}
                    </Typography>
                  </Box>
                </Box>

                {/* Processing Area */}
                <Box sx={{ p: 3 }}>
                  {isEtkOrder ? (
                    <SampleScannedNew
                      scannedId={scan}
                      onBack={clearScan} // Handle back navigation
                    />
                  ) : (
                    <SampleScanned
                      scanData={scan}
                      clearScan={clearScan}
                      webUrl={webUrl}
                    />
                  )}
                </Box>
              </CardContent>
            </Card>
          </Fade>
        </Container>
      </Box>
    );
  };

  return <>{!scan ? renderScanner() : renderInputFields()}</>;
}
